import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, useTable, EditButton, ShowButton } from '@refinedev/antd';
import { useDelete } from '@refinedev/core';
import { useList } from '@refinedev/core';
import { Table, Space, Tag, Checkbox, Button, Modal, Typography, message } from 'antd';

import { CSSProperties } from 'react';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { IRaceMaster } from '@/interfaces/race.interface';

export const RaceList: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: deleteRace } = useDelete();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [targetRace, setTargetRace] = useState<IRaceMaster | null>(null);

  const showDeleteConfirm = (race: IRaceMaster) => {
    setTargetRace(race);
    setDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setTargetRace(null);
  };

  const handleDelete = () => {
    if (!targetRace) return;
    
    setDeleteModalVisible(false);
    deleteRace(
      {
        resource: "race_master",
        id: targetRace.id,
      },
      {
        onSuccess: () => {
          message.success("レースを削除しました");
          setDeleteModalVisible(false);
        },
        onError: (error) => {
          message.error("レースの削除に失敗しました");
          console.error("Error deleting race:", error);
        },
      }
    );
  };
  const { tableProps } = useTable<IRaceMaster>({
    resource: "race_master",
    meta: {
      select: "*"
    },
    sorters: {
      initial: [
        {
          field: "race_date",
          order: "desc"
        }
      ]
    }
  });

  const { data } = useList<IRaceMaster>({
    resource: "race_master",
    queryOptions: {
      enabled: true,
    },
  });

  console.log("Fetched data:", data?.data);

  const columns: ColumnsType<IRaceMaster> = [
    {
      title: '公開ステータス',
      dataIndex: 'publication_status',
      key: 'publication_status',
      width: 120,
      align: 'center',
      render: (value: string) => {
        const statusMap = {
          draft: { text: '下書き', color: 'processing' },
          published: { text: '公開中', color: 'success' }
        };
        const status = statusMap[value as keyof typeof statusMap] || { text: value, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '公開日時',
      dataIndex: 'publication_date_time',
      key: 'publication_date_time',
      width: 160,
      align: 'center',
      render: (value: string) => value ? dayjs(value).format('YYYY/MM/DD HH:mm') : '-',
    },
    {
      title: 'レース名',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      align: 'left',
    },
    {
      title: '開催日',
      dataIndex: 'race_date',
      key: 'race_date',
      width: 120,
      align: 'center',
      render: (value: string) => value ? dayjs(value).format('YYYY/MM/DD') : '-',
    },
    {
      title: 'レース締切日',
      dataIndex: 'deadline_date',
      key: 'deadline_date',
      width: 120,
      align: 'center',
      render: (value: string) => value ? dayjs(value).format('YYYY/MM/DD') : '-',
    },
    {
      title: 'レースタイプ',
      dataIndex: 'is_dragon',
      key: 'race_type',
      width: 120,
      align: 'center',
      render: (value: boolean) => value ? 'ドラゴンレース' : '通常レース',
    },
    {
      title: '複数日開催',
      dataIndex: 'is_multiple',
      key: 'is_multiple',
      width: 100,
      align: 'center',
      render: (value: boolean) => <Checkbox checked={value} disabled />,
    },
    {
      title: '最終更新日',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      align: 'center',
      render: (value: string) => value ? dayjs(value).format('YYYY/MM/DD HH:mm') : '-',
    },
    {
      title: 'アクション',
      key: 'actions',
      fixed: 'right',
      width: 200,
      align: 'center',
      render: (_: any, record: IRaceMaster) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          <ShowButton 
            size="small" 
            recordItemId={record.id}
            href={`/races/show/${record.id}`}
            children="詳細"
          />
          <EditButton 
            size="small" 
            recordItemId={record.id}
            href={`/races/edit/${record.id}`}
            children="編集"
          />
          <Button 
            danger
            size="small"
            onClick={() => showDeleteConfirm(record)}
          >
            削除
          </Button>
        </Space>
      ),
    },
  ];

  const handleRowClick = (record: IRaceMaster) => {
    navigate(`/races/show/${record.id}`);
  };

  return (
    <>
      <List
        createButtonProps={{
          children: "新規レース作成",
          href: "/races/create"
        }}
      >
        <Table 
          {...tableProps} 
          columns={columns} 
          rowKey="id"
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
          }}
          scroll={{ x: 1400 }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' }
          })}
        />
      </List>

      <Modal
        title="レースを削除"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={handleDeleteCancel}
        okText="削除"
        cancelText="キャンセル"
        okButtonProps={{ danger: true }}
      >
        <Typography.Title level={5} style={{ marginBottom: 24 }}>
          {targetRace?.name}
        </Typography.Title>
        <Typography.Text>
          このレースを削除してもよろしいですか？
        </Typography.Text>
        <Typography.Text type="warning" style={{ display: 'block', marginTop: 16 }}>
          この操作は取り消すことができません。
        </Typography.Text>
      </Modal>
    </>
  );
};